export interface ChunkUploadOptions {
  file: File;
  chunkSize?: number; // Default 50MB chunks
  onProgress?: (progress: number) => void;
  onChunkProgress?: (chunkIndex: number, chunkProgress: number) => void;
  onError?: (error: Error) => void;
  apiUrl: string;
  headers?: Record<string, string>;
}

export interface ChunkUploadResult {
  success: boolean;
  uploadId?: string;
  error?: string;
}

export class ChunkedUploader {
  private static readonly DEFAULT_CHUNK_SIZE = 50 * 1024 * 1024; // 50MB
  private static readonly MAX_RETRIES = 3;

  static async uploadFile(options: ChunkUploadOptions): Promise<ChunkUploadResult> {
    const {
      file,
      chunkSize = ChunkedUploader.DEFAULT_CHUNK_SIZE,
      onProgress,
      onChunkProgress,
      onError,
      apiUrl,
      headers = {}
    } = options;

    try {
      // Step 1: Initialize multipart upload
      const initResponse = await fetch(`${apiUrl}/init-upload`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...headers
        },
        body: JSON.stringify({
          filename: file.name,
          fileSize: file.size,
          contentType: file.type
        })
      });

      if (!initResponse.ok) {
        throw new Error(`Failed to initialize upload: ${initResponse.statusText}`);
      }

      const { uploadId } = await initResponse.json();
      
      // Step 2: Upload chunks
      const chunks = Math.ceil(file.size / chunkSize);
      const uploadedChunks: string[] = [];

      for (let chunkIndex = 0; chunkIndex < chunks; chunkIndex++) {
        const start = chunkIndex * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const chunk = file.slice(start, end);

        let retries = 0;
        let chunkUploaded = false;

        while (retries < ChunkedUploader.MAX_RETRIES && !chunkUploaded) {
          try {
            const chunkFormData = new FormData();
            chunkFormData.append('chunk', chunk);
            chunkFormData.append('chunkIndex', chunkIndex.toString());
            chunkFormData.append('uploadId', uploadId);

            const chunkResponse = await fetch(`${apiUrl}/upload-chunk`, {
              method: 'POST',
              headers: {
                ...headers
              },
              body: chunkFormData
            });

            if (!chunkResponse.ok) {
              throw new Error(`Chunk ${chunkIndex} upload failed: ${chunkResponse.statusText}`);
            }

            const chunkResult = await chunkResponse.json();
            uploadedChunks[chunkIndex] = chunkResult.etag;
            chunkUploaded = true;

            // Report chunk progress
            onChunkProgress?.(chunkIndex, 100);
            
            // Report overall progress
            const overallProgress = ((chunkIndex + 1) / chunks) * 100;
            onProgress?.(overallProgress);

          } catch (error) {
            retries++;
            if (retries >= ChunkedUploader.MAX_RETRIES) {
              throw error;
            }
            // Wait before retry (exponential backoff)
            await new Promise(resolve => setTimeout(resolve, Math.pow(2, retries) * 1000));
          }
        }
      }

      // Step 3: Complete multipart upload
      const completeResponse = await fetch(`${apiUrl}/complete-upload`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...headers
        },
        body: JSON.stringify({
          uploadId,
          parts: uploadedChunks.map((etag, index) => ({
            ETag: etag,
            PartNumber: index + 1
          }))
        })
      });

      if (!completeResponse.ok) {
        throw new Error(`Failed to complete upload: ${completeResponse.statusText}`);
      }

      const result = await completeResponse.json();
      
      return {
        success: true,
        uploadId: result.vodId || uploadId
      };

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown upload error';
      onError?.(new Error(errorMessage));
      
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  static async abortUpload(uploadId: string, apiUrl: string, headers: Record<string, string> = {}): Promise<void> {
    try {
      await fetch(`${apiUrl}/abort-upload`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...headers
        },
        body: JSON.stringify({ uploadId })
      });
    } catch (error) {
      console.error('Failed to abort upload:', error);
    }
  }
}